import * as amplitude from "@amplitude/analytics-browser"
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser"
import { ExperimentClient, Experiment } from "@amplitude/experiment-js-client"
import { User } from "@ncs/ncs-api/portal-apps"
import { EVENTS_BY_CSS_CLASS_NAME, EVENTS_BY_ID, TrackingEvent } from "@ncs/web-legos/util"
import MetaPixelClient from "./metaPixel-client"
import ABTestingClient from "./abTesting-client"
import { AnalyticsIntegration } from "./types.analytics"

export type authUser = Omit<User, "apps"> & {
	punchOutSessionId: string | null
	companyId?: string | null
}

class AnalyticsClient {
	private static instance: AnalyticsClient
	private amplitudeInstance: amplitude.Types.BrowserClient

	private experimentInstance: ExperimentClient | null = null
	private experimentCurrentVariants: Record<string, unknown> = {}

	private integrations: AnalyticsIntegration[] = []

	private currentUser: authUser | null = null
	private initialized = false

	private constructor() {
		this.amplitudeInstance = amplitude.createInstance()
	}

	public static getInstance(): AnalyticsClient {
		if (!AnalyticsClient.instance) {
			AnalyticsClient.instance = new AnalyticsClient()
		}
		return AnalyticsClient.instance
	}

	public static getExperimentInstance(): ExperimentClient {
		return AnalyticsClient.getInstance()?.experimentInstance as ExperimentClient
	}

	public isInitialized(): boolean {
		return this.initialized
	}

	private initAmplitude(apiKey: string) {
		this.amplitudeInstance.init(apiKey, undefined, {
			autocapture: {
				elementInteractions: true,
			},
			identityStorage: "localStorage",
			defaultTracking: {
				sessions: true,
				pageViews: true,
				formInteractions: true,
				fileDownloads: true,
			},
		})

		this.amplitudeInstance.add(
			sessionReplayPlugin({
				sampleRate: 0.2,
			})
		)

		this.trackEventByHTMLElement()
	}

	private initExperiment(experimentApiKey: string) {
		if (!experimentApiKey) return
		this.experimentInstance = Experiment.initialize(experimentApiKey)
	}

	public init(apiKey: string, experimentApiKey: string): void {
		if (!apiKey) return
		this.initAmplitude(apiKey)
		this.initExperiment(experimentApiKey)

		// Initialize external integrations uniformly
		this.integrations = []

		const metaPixelId = import.meta.env.VITE_META_PIXEL_ID
		if (metaPixelId) {
			const metaPixel = new MetaPixelClient()
			metaPixel.initialize({ accountId: metaPixelId })
			this.integrations.push(metaPixel)
		}

		const amplitudeExperimentKey = import.meta.env.VITE_AMPLITUDE_KEY
		if (amplitudeExperimentKey) {
			const abClient = new ABTestingClient()
			abClient.initialize({ apiKey: amplitudeExperimentKey })
			this.integrations.push(abClient)
		}

		this.initialized = true
	}

	private setUser(user: authUser) {
		this.currentUser = user
		const emailDomain = user.email?.split("@")[1].split(".")[0]?.toLowerCase()
		this.currentUser.companyId = emailDomain ?? "Unknown"
	}

	public identify(user: authUser): void {
		if (!user.id) return
		this.setUser(user)

		const identifyEvent = new amplitude.Identify().set("email", user.email)
		if (this.currentUser?.companyId) {
			this.amplitudeInstance.setGroup("company_id", this.currentUser.companyId)
		}
		this.amplitudeInstance.setUserId(user.id)
		this.amplitudeInstance.identify(identifyEvent)
	}

	public trackEvent(
		event: TrackingEvent | string,
		data?: Record<string, any>,
		timeout = 0
	): void {
		setTimeout(() => {
			if (this.initialized) {
				this.amplitudeInstance.logEvent(event, data)
				this.integrations.forEach((integration) => {
					if (integration.isInitialized()) {
						integration.trackEvent(String(event), data ?? {})
					}
				})
			} else {
				this.trackEvent(event, data, timeout + 1000)
			}
		}, timeout)
	}

	private trackEventById(id: string): void {
		if (EVENTS_BY_ID[id]) {
			this.trackEvent(EVENTS_BY_ID[id])
		}
	}

	private trackEventByClassName(classList: DOMTokenList): void {
		Object.keys(EVENTS_BY_CSS_CLASS_NAME).forEach((cssClassName) => {
			if (classList.contains(cssClassName)) {
				this.trackEvent(EVENTS_BY_CSS_CLASS_NAME[cssClassName])
			}
		})
	}

	private trackEventByHTMLElement = (): void => {
		document.addEventListener("click", (event) => {
			const clickedElement: HTMLElement | null = event.target as HTMLElement
			const parentElement: HTMLElement | null = clickedElement?.parentElement

			setTimeout(() => {
				if (clickedElement?.id) {
					this.trackEventById(clickedElement.id)
				}

				if (parentElement?.id) {
					this.trackEventById(parentElement.id)
				}

				if (clickedElement?.classList?.length) {
					this.trackEventByClassName(clickedElement.classList)
				}

				if (parentElement?.classList?.length) {
					this.trackEventByClassName(parentElement.classList)
				}
			})
		})
	}

	public startExperiment(): void {
		if (!this.experimentInstance) return
		void this.experimentInstance.start()
	}

	public stopExperiment(): void {
		if (!this.experimentInstance) return
		void this.experimentInstance.stop()
	}

	public fetchExperiments(): void {
		if (!this.experimentInstance) return
		this.experimentCurrentVariants = this.experimentInstance.all() ?? {}
	}

	public reset(): void {
		this.currentUser = null
		this.amplitudeInstance.setUserId(undefined)
		this.amplitudeInstance.reset()
		this.integrations.forEach((integration) => integration.reset?.())
	}

	public getExperimentCurrentVariants(): Record<string, unknown> | null {
		return this.experimentCurrentVariants
	}

	public getExperimentVariantData(experimentName: string): Record<string, unknown> | null {
		if (!this.experimentCurrentVariants[experimentName]) return null
		return this.experimentCurrentVariants[experimentName] as Record<
			string,
			{ key: string; metadata: Record<string, unknown> }
		>
	}
}

export default AnalyticsClient
